import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import posthog from "posthog-js";

const LAST_SHOWN_KEY = "strategySessionModalLastShown";
const HAS_BOOKED_KEY = "hasBookedStrategySession";

export function useStrategySessionModal() {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();

  // Check if user has already booked a session
  const hasBookedSession = () => {
    return localStorage.getItem(HAS_BOOKED_KEY) === "true";
  };

  // Mark that user has booked a session
  const markAsBooked = () => {
    localStorage.setItem(HAS_BOOKED_KEY, "true");
  };

  // Check if modal was shown today
  const wasShownToday = () => {
    const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
    if (!lastShown) return false;
    
    const lastShownDate = new Date(lastShown);
    const today = new Date();
    
    return lastShownDate.toDateString() === today.toDateString();
  };

  // Mark modal as shown today
  const markAsShownToday = () => {
    localStorage.setItem(LAST_SHOWN_KEY, new Date().toISOString());
  };

  // Check if we're within 7 days of user's signup
  const isWithinPromoPeriod = () => {
    if (!user?.created) return false;
    
    const signupDate = new Date(user.created);
    const now = new Date();
    const daysSinceSignup = (now.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysSinceSignup <= 7;
  };

  // Open modal manually (e.g., from sidebar)
  const openModal = () => {
    setIsOpen(true);
    posthog.capture("strategy_session_modal_opened", {
      source: "manual",
    });
  };

  // Close modal
  const closeModal = () => {
    setIsOpen(false);
  };

  // Handle booking action
  const handleBook = () => {
    markAsBooked();
    posthog.capture("strategy_session_booked", {
      source: "modal",
    });
    window.open("https://cal.com/aicreator/30min", "_blank");
    closeModal();
  };

  // Auto-show logic - show daily for first 7 days unless booked
  useEffect(() => {
    if (isAuthenticated && user && isWithinPromoPeriod() && !hasBookedSession() && !wasShownToday()) {
      // Small delay to ensure smooth transition after login
      const timer = setTimeout(() => {
        setIsOpen(true);
        markAsShownToday();
        posthog.capture("strategy_session_modal_opened", {
          source: "auto",
          trigger: "daily_show",
        });
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user]);

  return {
    isOpen,
    openModal,
    closeModal,
    handleBook,
    isWithinPromoPeriod: isWithinPromoPeriod(),
    hasBookedSession: hasBookedSession(),
  };
}